import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileText,
  Plus,
  X,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Link2,
  PenLine,
  Trash2,
  Eye,
  ShieldCheck,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { waivers } from '../api/endpoints.js'

const EMPTY_WAIVER = {
  name: '',
  content: '',
  version: '1.0',
  is_required: true,
  is_active: true,
}

const fmt = (d) =>
  d ? new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '—'

const fmtTime = (d) =>
  d ? new Date(d).toLocaleString(undefined, {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }) : '—'

// ─────────────────────────────────────────────────────────────────────────────

function WaiverModal({ waiver = null, onClose }) {
  const qc = useQueryClient()
  const isEdit = !!waiver
  const [form, setForm] = useState(
    isEdit
      ? {
          name: waiver.name ?? '',
          content: waiver.content ?? '',
          version: waiver.version ?? '1.0',
          is_required: waiver.is_required ?? true,
          is_active: waiver.is_active ?? true,
        }
      : EMPTY_WAIVER,
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [preview, setPreview] = useState(false)

  const change = (e) => {
    const { name, value, type, checked } = e.target
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }))
  }

  const submit = async (e) => {
    e.preventDefault()
    if (!form.name.trim() || !form.content.trim()) {
      setError('Name and body are required.')
      return
    }
    setSaving(true)
    setError('')
    try {
      if (isEdit) {
        await waivers.update(waiver.id, form)
        toast.success('Waiver updated')
      } else {
        await waivers.create(form)
        toast.success('Waiver created')
      }
      qc.invalidateQueries({ queryKey: ['waivers'] })
      onClose()
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save waiver.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4">
      <div className="bg-surface-2 border border-surface-3 rounded-2xl w-full max-w-3xl elev-2 animate-fade-in overflow-y-auto max-h-[92vh]">
        <div className="flex items-center justify-between p-5 border-b border-surface-3 sticky top-0 bg-surface-2 z-10">
          <h2 className="text-lg font-semibold text-text-primary">
            {isEdit ? 'Edit Waiver' : 'New Waiver'}
          </h2>
          <div className="flex items-center gap-2">
            <button type="button" onClick={() => setPreview((p) => !p)}
                    className="btn-ghost px-2 py-1 rounded text-sm flex items-center gap-1.5">
              <Eye className="w-4 h-4" /> {preview ? 'Edit' : 'Preview'}
            </button>
            <button onClick={onClose} className="text-text-dim hover:text-text-primary">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          {error && (
            <p className="text-sm text-status-suspended bg-status-suspended/10 border border-status-suspended/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div className="grid grid-cols-[1fr_120px] gap-4">
            <div>
              <label className="label">Name *</label>
              <input name="name" className="input-field" value={form.name} onChange={change}
                     placeholder="Liability Waiver and Release" required />
            </div>
            <div>
              <label className="label">Version</label>
              <input name="version" className="input-field" value={form.version} onChange={change} />
            </div>
          </div>

          <div>
            <label className="label">Body (HTML) *</label>
            {preview ? (
              <div
                className="rounded-lg border border-surface-3 bg-surface p-4 max-h-[46vh] overflow-y-auto
                           text-sm text-text-muted [&_h2]:text-text-primary [&_h2]:font-semibold [&_h2]:mb-2
                           [&_h3]:text-text-primary [&_h3]:font-medium [&_h3]:mt-4 [&_h3]:mb-1 [&_p]:mb-2"
                dangerouslySetInnerHTML={{ __html: form.content }}
              />
            ) : (
              <textarea name="content" rows={16}
                        className="input-field font-mono text-xs leading-relaxed resize-y"
                        value={form.content} onChange={change}
                        placeholder="<h2>Liability Waiver</h2><p>…</p>" />
            )}
          </div>

          <div className="flex gap-6">
            <label className="flex items-center gap-2 text-sm text-text-muted">
              <input type="checkbox" name="is_required" checked={form.is_required}
                     onChange={change} className="accent-primary w-4 h-4" />
              Required before check-in
            </label>
            <label className="flex items-center gap-2 text-sm text-text-muted">
              <input type="checkbox" name="is_active" checked={form.is_active}
                     onChange={change} className="accent-primary w-4 h-4" />
              Make this the current waiver
            </label>
          </div>
          {form.is_active && (
            <p className="text-xs text-text-dim">
              Activating this retires the previous waiver. Existing signatures are kept.
            </p>
          )}

          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary flex-1">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary flex-1">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : isEdit ? 'Save' : 'Create waiver'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function Unsigned() {
  const { data, isLoading } = useQuery({
    queryKey: ['waivers', 'unsigned'],
    queryFn: () => waivers.unsigned().then((r) => r.data),
  })

  const copy = (url) => {
    const full = `${window.location.origin}${url}`
    navigator.clipboard?.writeText(full)
      .then(() => toast.success('Signing link copied'))
      .catch(() => toast.error('Could not copy link'))
  }

  if (isLoading) {
    return <div className="card p-8 text-center">
      <Loader2 className="w-5 h-5 animate-spin mx-auto text-text-dim" />
    </div>
  }

  if ((data || []).length === 0) {
    return (
      <div className="card p-10 text-center">
        <CheckCircle2 className="w-8 h-8 mx-auto text-status-active mb-3" />
        <p className="text-text-primary font-medium">Every active member has signed.</p>
        <p className="text-sm text-text-dim">Nothing to chase.</p>
      </div>
    )
  }

  return (
    <div className="table-container">
      <table className="table-base">
        <thead className="table-head">
          <tr><th>Member</th><th>Email</th><th>Phone</th><th>Status</th><th>Joined</th><th /></tr>
        </thead>
        <tbody>
          {data.map((m) => (
            <tr key={m.id} className="table-row cursor-default">
              <td className="font-medium">{m.first_name} {m.last_name}</td>
              <td className="text-text-muted">{m.email}</td>
              <td className="text-text-muted">{m.phone || '—'}</td>
              <td className="text-text-muted capitalize">{m.status || '—'}</td>
              <td className="text-text-muted">{fmt(m.created_at)}</td>
              <td>
                <div className="flex gap-1 justify-end">
                  <button className="btn-ghost px-2 py-1 rounded text-xs flex items-center gap-1.5"
                          onClick={() => copy(m.sign_url)}>
                    <Link2 className="w-3.5 h-3.5" /> Copy link
                  </button>
                  <a className="btn-ghost px-2 py-1 rounded text-xs flex items-center gap-1.5 text-primary"
                     href={m.sign_url} target="_blank" rel="noreferrer">
                    <PenLine className="w-3.5 h-3.5" /> Sign now
                  </a>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function Signatures() {
  const { data, isLoading } = useQuery({
    queryKey: ['waivers', 'signatures'],
    queryFn: () => waivers.signatures({ limit: 200 }).then((r) => r.data),
  })

  if (isLoading) {
    return <div className="card p-8 text-center">
      <Loader2 className="w-5 h-5 animate-spin mx-auto text-text-dim" />
    </div>
  }

  if ((data || []).length === 0) {
    return <div className="card p-10 text-center text-text-dim">No signatures recorded yet.</div>
  }

  return (
    <div className="table-container">
      <table className="table-base">
        <thead className="table-head">
          <tr><th>Member</th><th>Waiver</th><th>Signed</th><th>IP</th><th>Signature</th></tr>
        </thead>
        <tbody>
          {data.map((s) => (
            <tr key={s.id} className="table-row cursor-default">
              <td>
                <span className="font-medium">{s.member_name || '—'}</span>
                <span className="block text-xs text-text-dim">{s.member_email}</span>
              </td>
              <td className="text-text-muted">{s.waiver_name || '—'}</td>
              <td className="text-text-muted whitespace-nowrap">{fmtTime(s.signed_at)}</td>
              <td className="text-text-muted font-mono text-xs">{s.ip_address || '—'}</td>
              <td>
                {s.signature_data?.startsWith('data:image') ? (
                  <img src={s.signature_data} alt="signature"
                       className="h-8 bg-white rounded px-1" />
                ) : (
                  <span className="italic text-text-muted">{s.signature_data || '—'}</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

const TABS = [
  ['documents', 'Documents', FileText],
  ['unsigned', 'Not signed', AlertTriangle],
  ['signatures', 'Signatures', ShieldCheck],
]

export default function WaiversPage() {
  const qc = useQueryClient()
  const [tab, setTab] = useState('documents')
  const [adding, setAdding] = useState(false)
  const [editing, setEditing] = useState(null)

  const { data: list, isLoading } = useQuery({
    queryKey: ['waivers', 'list'],
    queryFn: () => waivers.list().then((r) => r.data),
  })
  const { data: compliance } = useQuery({
    queryKey: ['waivers', 'compliance'],
    queryFn: () => waivers.compliance().then((r) => r.data),
  })

  const activate = useMutation({
    mutationFn: (id) => waivers.activate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waivers'] })
      toast.success('Waiver activated')
    },
    onError: () => toast.error('Could not activate waiver'),
  })

  const retire = useMutation({
    mutationFn: (id) => waivers.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['waivers'] })
      toast.success('Waiver retired')
    },
    onError: () => toast.error('Could not retire waiver'),
  })

  const openEdit = async (w) => {
    // the list endpoint omits the body — fetch the full record before editing
    try {
      const full = await waivers.get(w.id).then((r) => r.data)
      setEditing(full)
    } catch {
      toast.error('Could not load waiver body')
    }
  }

  const pct = compliance?.percent_signed ?? 0

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Waivers</h1>
          <p className="text-sm text-text-muted mt-1">
            The liability document members sign, and who still owes you one.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex gap-1 p-1 rounded-xl bg-surface-2 border border-surface-3">
            {TABS.map(([v, l, Icon]) => (
              <button key={v} onClick={() => setTab(v)}
                      className={['tab-btn flex items-center gap-2',
                        tab === v ? 'tab-btn-active' : 'tab-btn-inactive'].join(' ')}>
                <Icon className="w-4 h-4" /> {l}
              </button>
            ))}
          </div>
          <button onClick={() => setAdding(true)} className="btn-primary whitespace-nowrap">
            <Plus className="w-4 h-4" /> New waiver
          </button>
        </div>
      </div>

      <div className="card p-4 mb-6">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs text-text-dim uppercase tracking-wide">Compliance</p>
            <p className="text-2xl font-bold text-text-primary">
              {compliance?.signed ?? 0}
              <span className="text-base font-medium text-text-dim"> / {compliance?.total_members ?? 0} signed</span>
            </p>
            {compliance?.active_waiver_name && (
              <p className="text-xs text-text-dim mt-1">
                Current document: <span className="text-text-muted">{compliance.active_waiver_name}</span>
              </p>
            )}
          </div>
          <div className="flex-1 min-w-[200px]">
            <div className="h-2 rounded-full bg-surface-3 overflow-hidden">
              <div
                className={[
                  'h-full rounded-full transition-all duration-700',
                  pct >= 95 ? 'bg-status-active' : pct >= 70 ? 'bg-status-alert' : 'bg-status-suspended',
                ].join(' ')}
                style={{ width: `${pct}%` }}
              />
            </div>
            <p className="text-xs text-text-dim mt-1 text-right">
              {pct}% · {compliance?.unsigned ?? 0} outstanding
            </p>
          </div>
        </div>
      </div>

      {tab === 'documents' && (
        <div className="table-container">
          <table className="table-base">
            <thead className="table-head">
              <tr><th>Document</th><th>Version</th><th>Required</th><th>Signatures</th><th>Created</th><th /></tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={6} className="px-4 py-8 text-center">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto text-text-dim" />
                </td></tr>
              )}
              {!isLoading && (list || []).length === 0 && (
                <tr><td colSpan={6} className="px-4 py-10 text-center text-text-dim">
                  <FileText className="w-7 h-7 mx-auto mb-2 opacity-60" />
                  No waiver on file — the public signing page falls back to the built-in default.
                </td></tr>
              )}
              {(list || []).map((w) => (
                <tr key={w.id} className="table-row" onClick={() => openEdit(w)}>
                  <td>
                    <span className="font-medium">{w.name}</span>
                    {w.is_active && <span className="badge-active ml-2">Current</span>}
                  </td>
                  <td className="text-text-muted">v{w.version}</td>
                  <td className="text-text-muted">{w.is_required ? 'Yes' : 'Optional'}</td>
                  <td className="text-text-muted">{w.signature_count}</td>
                  <td className="text-text-muted">{fmt(w.created_at)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div className="flex gap-1 justify-end">
                      {!w.is_active && (
                        <button className="btn-ghost px-2 py-1 rounded text-xs text-primary"
                                onClick={() => activate.mutate(w.id)}>
                          Make current
                        </button>
                      )}
                      <button
                        className="btn-ghost p-1.5 rounded text-text-dim hover:text-status-suspended"
                        onClick={() => {
                          if (confirm(`Retire "${w.name}"? Signatures are kept.`)) retire.mutate(w.id)
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === 'unsigned' && <Unsigned />}
      {tab === 'signatures' && <Signatures />}

      {adding && <WaiverModal onClose={() => setAdding(false)} />}
      {editing && <WaiverModal waiver={editing} onClose={() => setEditing(null)} />}
    </div>
  )
}
